import { V2Component } from './autoSelectTypes'

export function getPreferredSupplierKeyForComponent(
  component: V2Component,
  orderedSuppliers: string[]
): string | undefined {
  for (const supplier of orderedSuppliers) {
    if (component.ordering_v2?.some((ordering) => ordering.distributor === supplier)) {
      return supplier
    }
  }
  return undefined
}

export function componentIsFromPreferredSupplier(component: V2Component, orderedSuppliers: string[]): boolean {
  return getPreferredSupplierKeyForComponent(component, orderedSuppliers) != null
}

export function componentIsFromSupplier(component: V2Component, supplier: string): boolean {
  return component.ordering_v2?.some((ordering) => ordering.distributor === supplier) ?? false
}

/**
 * Supplier walk order for isolator auto-select: preference list first, then any
 * other distributors present on the candidate components (stable, sorted).
 */
export function getSupplierAttemptOrder(
  components: V2Component[],
  orderedSuppliers: string[],
  preferredSuppliersOnly: boolean
): string[] {
  if (preferredSuppliersOnly) {
    return orderedSuppliers
  }

  if (!orderedSuppliers.length) {
    const seen = new Set<string>()
    const distributors: string[] = []
    for (const component of components) {
      for (const row of component.ordering_v2 ?? []) {
        if (!seen.has(row.distributor)) {
          seen.add(row.distributor)
          distributors.push(row.distributor)
        }
      }
    }
    return distributors
  }

  const others = new Set<string>()
  for (const component of components) {
    for (const row of component.ordering_v2 ?? []) {
      if (!orderedSuppliers.includes(row.distributor)) {
        others.add(row.distributor)
      }
    }
  }

  return [...orderedSuppliers, ...[...others].sort()]
}

/** Walk `orderedSuppliers` in org preference order; return first stocked catalog row. */
export function pickFirstAtPreferredSupplier(
  components: V2Component[],
  orderedSuppliers: string[],
  requirePreferredSupplier: boolean
): V2Component | null {
  if (!components.length) return null
  if (!orderedSuppliers.length) return components[0]

  const suppliersToTry = getSupplierAttemptOrder(components, orderedSuppliers, requirePreferredSupplier)

  for (const supplier of suppliersToTry) {
    const match = components.find((component) => componentIsFromSupplier(component, supplier))
    if (match) return match
  }

  return null
}

export type CatalogFetchScope = 'preferred_suppliers' | 'full_catalog'

export async function selectFromCatalogWithSupplierFallback<C, T>(params: {
  orderedSuppliers: string[]
  fetchComponents: (scopedToPreferredSuppliers: boolean) => Promise<C[]>
  refineComponents?: (components: C[]) => C[]
  pick: (components: C[], preferredSuppliersOnly: boolean) => T | null
  fallbackPick?: (components: C[]) => T | null
}): Promise<{ match: T | null; totalFound: number; catalogFetchScope: CatalogFetchScope }> {
  const refine = params.refineComponents ?? ((components) => components)
  const hasPreferredSuppliers = params.orderedSuppliers.length > 0

  let components = refine(await params.fetchComponents(hasPreferredSuppliers))
  let match = params.pick(components, hasPreferredSuppliers)
  if (match) {
    return { match, totalFound: components.length, catalogFetchScope: 'preferred_suppliers' }
  }

  if (hasPreferredSuppliers) {
    components = refine(await params.fetchComponents(false))
    match = params.pick(components, false)
    if (match) {
      return { match, totalFound: components.length, catalogFetchScope: 'full_catalog' }
    }
  }

  if (!match && components.length > 0 && params.fallbackPick) {
    match = params.fallbackPick(components)
  }

  return {
    match,
    totalFound: components.length,
    catalogFetchScope: hasPreferredSuppliers ? 'full_catalog' : 'preferred_suppliers',
  }
}
